import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../../integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Button } from "../../../ui/button";
import { OutletMultiSelect } from "./dashboard/OutletMultiSelect";
import { Progress } from "../../../ui/progress";
import { Loader2, RefreshCw, Settings, Heart, Sparkles } from "lucide-react";
import { useDashboardRefresh } from "./dashboard/DashboardHeader";
import { PerformanceOverview } from "./dashboard/PerformanceOverview";
import { ReviewInbox } from "./dashboard/ReviewInbox";
import { UrgentReviews } from "./dashboard/UrgentReviews";
import { WallOfLoveGallery } from "./walloflove/WallOfLoveGallery";
import { WidgetGenerator } from "./walloflove/WidgetGenerator";

import { AnalyticsTab } from "./tabs/AnalyticsTab";
import { toast } from "sonner";
import { useDashboardInsights, PERIOD_LABELS, type PeriodDays } from "../../../../hooks/useDashboardInsights";
import { ImpactBanner } from "./dashboard/insights/ImpactBanner";
import { GrowthImpactRow } from "./dashboard/insights/GrowthImpactRow";
import { BusinessImpact } from "./dashboard/insights/BusinessImpact";
import { BeforeAfter } from "./dashboard/insights/BeforeAfter";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
interface DashboardProps {
  props: any;
  supabaseoutletIDs:string[];
}

const Dashboard  = ({ props, supabaseoutletIDs }: DashboardProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [outlets, setOutlets] = useState<any[]>([]);
  const [selectedOutlets, setSelectedOutlets] = useState<string[]>(["all"]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [wallReviews, setWallReviews] = useState<any[]>([]);
  const [wallOfLoveTab, setWallOfLoveTab] = useState("gallery");
  const [isAutoCurating, setIsAutoCurating] = useState(false);
   const [periodDays, setPeriodDays] = useState<PeriodDays>(7);
  const outlets_= useMemo(() => [selectedOutlets], [selectedOutlets]);
  const { insights, loading: insightsLoading } = useDashboardInsights(outlets_, periodDays, supabaseoutletIDs);

  useEffect(() => {
    checkProfile();
    loadOutlets();
  }, []);

  useEffect(() => {
    if (activeTab === "walloflove") {
      loadWallReviews();
    }
  }, [activeTab, selectedOutlets]);

  const checkProfile = async () => {
    try {
      const { data/* , error */ } = await supabase
        .from("business_profile")
        .select("*")
        .maybeSingle();

      if (!data) {
        //navigate("/setup");
        return;
      }

      setProfile(data);
    } catch (error) {
      console.error("Error checking profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const { handleRefresh, scraping, progress, currentPlatform } =
    useDashboardRefresh(checkProfile);

  const loadOutlets = async () => {
    try {
      const { data, error } = await supabase
        .from("outlets")
        .select("*").in('id', supabaseoutletIDs) 
        .order("outlet_name");

      if (error) throw error;
      setOutlets(data || []);
    } catch (error) {
      console.error("Error loading outlets:", error);
    }
  };

  const loadWallReviews = async () => {
    try {
      let query = supabase
        .from("reviews")
        .select("*, outlets(outlet_name)")
        .gte("rating", 4)
        .eq("sentiment", "positive")
        .order("posted_at", { ascending: false });

      const isAllSelected = selectedOutlets.length === 0 || selectedOutlets.flat().includes("all");
      if (!isAllSelected && selectedOutlets.length > 0) {
        query = query.in("outlet_id", selectedOutlets);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs) ;
      }

      const { data } = await query;
      setWallReviews(data || []);
    } catch (error) {
      console.error("Error loading wall reviews:", error);
    }
  };

  const handleAutoCurate = async () => {
    setIsAutoCurating(true);
    try {
      // First, unfeature all reviews
      await supabase
        .from("reviews")
        .update({ is_featured: false })
        .neq("id", "00000000-0000-0000-0000-000000000000");

      // Then auto-feature 4-5 star reviews with positive sentiment
      let query = supabase
        .from("reviews")
        .update({ is_featured: true })
        .gte("rating", 4)
        .eq("sentiment", "positive");

      const isAllSelected = !selectedOutlets ||  selectedOutlets.length === 0 ||  selectedOutlets.flat().includes("all");
      if (!isAllSelected && selectedOutlets.length > 0) {
        query = query.in("outlet_id", selectedOutlets);
      }else {
        query = query.in('outlet_id', supabaseoutletIDs) ;
      }

      const { error } = await query;

      if (error) throw error;

      const scope = isAllSelected 
        ? "all outlets" 
        : selectedOutlets.length === 1 
          ? "this outlet" 
          : `${selectedOutlets.length} selected outlets`;
      toast.success(`All 4-5 star reviews with positive sentiment from ${scope} have been added to the Wall of Love`);

      await loadWallReviews();
    } catch (error) {
      console.error("Error auto-curating reviews:", error);
      toast.error("Failed to auto-curate reviews");
    } finally {
      setIsAutoCurating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold">Review{/* {profile?.business_name} */}</h1>
            <p className="text-sm text-muted-foreground">
              {profile?.last_scraped_at ? `Last updated: ${new Date(profile.last_scraped_at).toLocaleString()}` : "No data yet"}
            </p>
          </div>
          
          {scraping && (
            <div className="space-y-2 mt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {currentPlatform ? `Analysing ${currentPlatform}...` : "Starting..."}
                </span>
                <span className="font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}
        </div>
      </header>

      <div className="container mx-auto p-6">
        {/* Outlet Selector with Actions */}
        <div className="mb-6 flex items-center gap-4">
          <label className="text-sm font-medium">View Data For:</label>
          <OutletMultiSelect
            outlets={outlets}
            selectedOutlets={selectedOutlets}
            onChange={setSelectedOutlets}
          />
          <div className="ml-auto flex gap-2">
            <Button onClick={handleRefresh} disabled={scraping}>
              <RefreshCw
                className={`h-4 w-4 mr-2 ${scraping ? "animate-spin" : ""}`}
              />
              Refresh Reviews
            </Button>
            <Button variant="outline" onClick={() => props.navigate("/clientpanel/rating/setting")}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="urgent">Urgent Reviews</TabsTrigger>
            <TabsTrigger value="walloflove">Wall of Love</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Comparing period:
              </span>
              <ToggleGroup
                type="single"
                value={String(periodDays)}
                onValueChange={(v) =>
                  v && setPeriodDays(Number(v) as PeriodDays)
                }
                className="bg-muted rounded-lg p-0.5"
              >
                <ToggleGroupItem
                  value="7"
                  className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                >
                  7 days
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="30"
                  className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                >
                  30 days
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="90"
                  className="text-xs px-3 h-7 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                >
                  90 days
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <ImpactBanner
              insights={insights}
              loading={insightsLoading}
              periodDays={periodDays}
            />
            <GrowthImpactRow
              insights={insights}
              loading={insightsLoading}
              periodDays={periodDays}
            />
            <BusinessImpact insights={insights} loading={insightsLoading} />
            <BeforeAfter insights={insights} loading={insightsLoading} />
            <PerformanceOverview
              selectedOutlets={selectedOutlets}
              onNavigateToUrgent={() => setActiveTab("urgent")}
              supabaseoutletIDs={supabaseoutletIDs}
            />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <AnalyticsTab selectedOutlets={selectedOutlets} supabaseoutletIDs={supabaseoutletIDs} />
          </TabsContent>

          <TabsContent value="reviews" className="space-y-6">
            <ReviewInbox preselectedOutlets={selectedOutlets} supabaseoutletIDs={supabaseoutletIDs} />
          </TabsContent>

          <TabsContent value="urgent" className="space-y-6">
            <UrgentReviews selectedOutlets={selectedOutlets} supabaseoutletIDs={supabaseoutletIDs}  />
          </TabsContent>

          <TabsContent value="walloflove" className="space-y-6">
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="h-6 w-6 text-red-500 fill-red-500" />
                <h2 className="text-2xl font-bold">Wall of Love</h2>
              </div>
              <p className="text-muted-foreground mb-6">
                Curate and showcase your best reviews with an embeddable widget
              </p>

              <Tabs
                value={wallOfLoveTab}
                onValueChange={setWallOfLoveTab}
                className="space-y-6"
              >
                <div className="flex items-center justify-between">
                  <TabsList>
                    <TabsTrigger value="gallery">Review Gallery</TabsTrigger>
                    <TabsTrigger value="embed">Embed Widget</TabsTrigger>
                  </TabsList>

                  {wallOfLoveTab === "embed" && (
                    <Button
                      onClick={handleAutoCurate}
                      disabled={isAutoCurating}
                      className="gap-2"
                      variant="outline"
                    >
                      {isAutoCurating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      Auto-Curate Best Reviews
                    </Button>
                  )}
                </div>

                <TabsContent value="gallery">
                  <WallOfLoveGallery
                    reviews={wallReviews}
                    onUpdate={loadWallReviews}
                  />
                </TabsContent>

                <TabsContent value="embed">
                  <WidgetGenerator
                    outlets={outlets}
                    selectedOutlets={selectedOutlets}
                    supabaseoutletIDs={supabaseoutletIDs}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

function GscStatusDot() {
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    supabase
      .from("gsc_connections")
      .select("site_url")
      .maybeSingle()
      .then(({ data }) => setConnected(!!data?.site_url));
  }, []);
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${
        connected ? "bg-emerald-500" : "bg-muted-foreground/40"
      }`}
    />
  );
}

export default Dashboard;
